import React, { type ComponentType } from 'react';
import '@atlaskit/css-reset';
import AppBase from '@atlassian/jira-app-base/src/index.tsx';
import ProjectContextProvider from '@atlassian/jira-business-entity-project-provider/src/ui/project-context/index.tsx';
import { TIMELINE_VIEW_PREFERENCES } from '@atlassian/jira-business-preferences/src/constants.tsx';
import { ViewPreferencesProvider } from '@atlassian/jira-business-preferences/src/controllers/view-preferences-context/index.tsx';
import TimelineFeaturesProvider from '@atlassian/jira-business-timeline/src/controllers/features-context/index.tsx';
import TimelineView from '@atlassian/jira-business-timeline/src/main.tsx';
import { useIsSubtasksEnabled } from '@atlassian/jira-router-resources-business-configuration/src/index.tsx';
import { useProject } from '@atlassian/jira-router-resources-business-project-details/src/index.tsx';
import { SubProductUpdater } from '@atlassian/jira-spa-apps-common/src/analytics-sub-product/sub-product-updater/index.tsx';
import { AuthenticationCheck } from '@atlassian/jira-spa-apps-common/src/interceptors/authentication/index.tsx';
import UFOSegment from '@atlassian/jira-ufo-segment/src/index.tsx';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type JiraBusinessTimelineAppType = Record<any, any>;

const JiraBusinessTimelineApp: ComponentType<JiraBusinessTimelineAppType> = () => {
	const { data: project } = useProject();

	const canLinkIssues = Boolean(project?.permissions.linkIssues);
	const canScheduleIssues = Boolean(project?.permissions.scheduleIssues);
	const canCreateIssues = Boolean(project?.permissions.createIssues);
	const isSubtasksEnabled = useIsSubtasksEnabled();
	const isSimplifiedProject = Boolean(project?.isSimplified);
	const canCreateSubtask = canCreateIssues && (isSimplifiedProject || isSubtasksEnabled);

	return (
		<UFOSegment name="jira-business-timeline-app">
			<AuthenticationCheck>
				<AppBase id="business">
					<ProjectContextProvider>
						<TimelineFeaturesProvider
							hasInlineCreate={canCreateIssues}
							hasCreateSubtask={canCreateSubtask}
							hasIssueLinking={canLinkIssues}
							hasScheduleIssues={canScheduleIssues}
						>
							<ViewPreferencesProvider view="timeline" preferences={TIMELINE_VIEW_PREFERENCES}>
								<TimelineView />
							</ViewPreferencesProvider>
						</TimelineFeaturesProvider>
					</ProjectContextProvider>
					<SubProductUpdater subProduct="core" />
				</AppBase>
			</AuthenticationCheck>
		</UFOSegment>
	);
};

export default JiraBusinessTimelineApp;
